// @ts-nocheck
import type { FieldExpressionBinding } from '../../types/canonical';
import { collectItemMetricRefsFromAstRoot } from '../metrics/collectItemMetricRefs';
import type { ExpressionDefinition } from '../../types/canonical';

function expressionRootById(
  expressions: readonly ExpressionDefinition[] | undefined,
  expressionId: string,
): Record<string, unknown> | undefined {
  return expressions?.find((e) => e.id === expressionId)?.root as Record<string, unknown> | undefined;
}

function collectItemFieldRefsFromAst(
  root: Record<string, unknown> | undefined,
  out: Set<string>,
): void {
  if (!root || typeof root !== 'object') {
    return;
  }
  if (root.kind === 'ref') {
    const ref = root.ref as { kind?: string; field?: string } | undefined;
    if (ref?.kind === 'itemField' && typeof ref.field === 'string') {
      out.add(ref.field);
    }
    return;
  }
  if (root.kind === 'unary') {
    collectItemFieldRefsFromAst(root.arg as Record<string, unknown>, out);
    return;
  }
  if (root.kind === 'binary') {
    collectItemFieldRefsFromAst(root.left as Record<string, unknown>, out);
    collectItemFieldRefsFromAst(root.right as Record<string, unknown>, out);
    return;
  }
  if (root.kind === 'if') {
    collectItemFieldRefsFromAst(root.when as Record<string, unknown>, out);
    collectItemFieldRefsFromAst(root.then as Record<string, unknown>, out);
    collectItemFieldRefsFromAst(root.else as Record<string, unknown>, out);
    return;
  }
  if (root.kind === 'callPure' && Array.isArray(root.args)) {
    for (const arg of root.args) {
      if (arg && typeof arg === 'object') {
        collectItemFieldRefsFromAst(arg as Record<string, unknown>, out);
      }
    }
  }
}

function bindingTargetKey(binding: FieldExpressionBinding): string | null {
  if (binding.target.kind === 'itemField') {
    return `itemField:${binding.target.field}`;
  }
  if (binding.target.kind === 'categoryProperty') {
    return `category:${binding.target.categoryId}.${binding.target.propertyId}`;
  }
  return null;
}

export interface FieldBindingCycleIssue {
  bindingId: string;
  message: string;
}

export function detectFieldBindingCycles(
  bindings: readonly FieldExpressionBinding[],
  expressions: readonly ExpressionDefinition[] | undefined,
): FieldBindingCycleIssue[] {
  const issues: FieldBindingCycleIssue[] = [];
  const targetByKey = new Map<string, string>();

  for (const binding of bindings) {
    const key = bindingTargetKey(binding);
    if (key) {
      targetByKey.set(key, binding.id);
    }
  }

  for (const binding of bindings) {
    if (binding.value.kind !== 'expression') {
      continue;
    }
    const root = expressionRootById(expressions, binding.value.expressionId);
    const readFields = new Set<string>();
    collectItemFieldRefsFromAst(root, readFields);

    if (binding.target.kind === 'itemField' && readFields.has(binding.target.field)) {
      issues.push({
        bindingId: binding.id,
        message: `Field binding '${binding.id}' reads itemField '${binding.target.field}' in its own expression.`,
      });
    }

    for (const field of readFields) {
      const otherId = targetByKey.get(`itemField:${field}`);
      if (otherId && otherId !== binding.id && binding.target.kind === 'itemField') {
        const other = bindings.find((b) => b.id === otherId);
        if (
          other?.value.kind === 'expression' &&
          other.target.kind === 'itemField' &&
          other.target.field === binding.target.field
        ) {
          issues.push({
            bindingId: binding.id,
            message: `Mutual field binding cycle detected between '${binding.id}' and '${otherId}'.`,
          });
        }
      }
    }
  }

  return issues;
}

export function collectExpressionIdsFromBindings(
  bindings: readonly FieldExpressionBinding[],
): string[] {
  return bindings
    .filter((b): b is FieldExpressionBinding & { value: { kind: 'expression'; expressionId: string } } =>
      b.value.kind === 'expression',
    )
    .map((b) => b.value.expressionId);
}

/** Expose AST metric walk for validator tests. */
export { collectItemMetricRefsFromAstRoot };
